import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Breadcrumb } from "@/components/layout/Breadcrumb";
import { useCurrentOrgId } from "@/hooks/useSkillOS";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Settings2, TrendingUp, AlertTriangle } from "lucide-react";

interface PolicyRow {
  id: string;
  task_class: string;
  preferred_provider: string;
  preferred_model: string;
  fallback_provider: string | null;
  fallback_model: string | null;
  max_tokens: number | null;
  temperature_default: number | null;
  sla_max_latency_ms: number | null;
}

interface DecisionRow {
  model_used: string;
  task_class: string;
  latency_ms: number | null;
  estimated_cost_usd: number | null;
  success: boolean | null;
}

export default function ModelPolicies() {
  const { data: orgId } = useCurrentOrgId();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [editing, setEditing] = useState<PolicyRow | null>(null);

  const { data: policies = [], isLoading: loadingPolicies } = useQuery({
    queryKey: ["model-policies", "list", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async (): Promise<PolicyRow[]> => {
      const { data, error } = await supabase
        .from("model_policies")
        .select("id, task_class, preferred_provider, preferred_model, fallback_provider, fallback_model, max_tokens, temperature_default, sla_max_latency_ms")
        .eq("org_id", orgId!)
        .order("task_class");
      if (error) throw error;
      return (data ?? []) as PolicyRow[];
    },
  });

  const { data: decisions = [], isLoading: loadingDecisions } = useQuery({
    queryKey: ["model-policies", "decisions-7d", orgId ?? "no-org"],
    enabled: !!orgId,
    queryFn: async (): Promise<DecisionRow[]> => {
      const since = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();
      const { data, error } = await supabase
        .from("model_decisions")
        .select("model_used, task_class, latency_ms, estimated_cost_usd, success")
        .eq("org_id", orgId!)
        .gte("created_at", since)
        .limit(2000);
      if (error) throw error;
      return (data ?? []) as DecisionRow[];
    },
  });

  const editMut = useMutation({
    mutationFn: async (vars: { id: string; changes: Record<string, unknown> }) => {
      const { data, error } = await supabase.rpc("edit_model_policy", {
        p_policy_id: vars.id,
        p_changes: vars.changes as never,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({ title: "Política atualizada" });
      qc.invalidateQueries({ queryKey: ["model-policies"] });
      setEditing(null);
    },
    onError: (e: Error) => toast({ title: "Falha ao atualizar", description: e.message, variant: "destructive" }),
  });

  const byTaskClass = useMemo(() => {
    const map = new Map<string, Map<string, { count: number; totalMs: number; totalCost: number; successes: number }>>();
    for (const d of decisions) {
      if (!map.has(d.task_class)) map.set(d.task_class, new Map());
      const tcMap = map.get(d.task_class)!;
      const cur = tcMap.get(d.model_used) ?? { count: 0, totalMs: 0, totalCost: 0, successes: 0 };
      cur.count++;
      cur.totalMs += d.latency_ms ?? 0;
      cur.totalCost += d.estimated_cost_usd ?? 0;
      if (d.success) cur.successes++;
      tcMap.set(d.model_used, cur);
    }
    const out = new Map<string, Array<{ model: string; count: number; avgMs: number; avgCost: number; successRate: number }>>();
    for (const [tc, models] of map) {
      out.set(tc, Array.from(models.entries()).map(([model, s]) => ({
        model,
        count: s.count,
        avgMs: Math.round(s.totalMs / s.count),
        avgCost: s.totalCost / s.count,
        successRate: Math.round((s.successes / s.count) * 100),
      })).sort((a, b) => b.count - a.count));
    }
    return out;
  }, [decisions]);

  const isLoading = loadingPolicies || loadingDecisions;

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "Skill OS" }, { label: "Model Policies" }]} />
      <div>
        <h1 className="text-2xl font-bold text-foreground font-headline flex items-center gap-2">
          <Settings2 className="w-6 h-6" /> Model Policies
        </h1>
        <p className="text-muted-foreground text-sm">
          Roteamento padrão de modelos por task class. Edite preferido, fallback, temperatura e SLA.
        </p>
      </div>

      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          Mudanças aqui afetam <strong>toda a sua organização</strong>. Owners e admins podem editar.
        </AlertDescription>
      </Alert>

      {!orgId ? (
        <EmptyState message="Sem organização ativa." />
      ) : isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-56" />)}
        </div>
      ) : !policies.length ? (
        <EmptyState message="Nenhuma política configurada." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {policies.map((p) => {
            const usageList = byTaskClass.get(p.task_class) ?? [];
            return (
              <Card key={p.id}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" /> <Badge variant="secondary">{p.task_class}</Badge>
                    </div>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Preferido</p>
                    <p className="font-mono text-xs truncate">{p.preferred_provider}/{p.preferred_model}</p>
                  </div>
                  {p.fallback_model && (
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">Fallback</p>
                      <p className="font-mono text-xs truncate">{p.fallback_provider}/{p.fallback_model}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 pt-2 border-t border-border/50">
                    <Stat label="Temp" value={p.temperature_default?.toString() ?? "—"} />
                    <Stat label="Max tokens" value={p.max_tokens?.toString() ?? "—"} />
                    <Stat label="SLA ms" value={p.sla_max_latency_ms?.toString() ?? "—"} />
                  </div>
                  {usageList.length > 0 && (
                    <div className="pt-3 border-t border-border/50 space-y-1">
                      <p className="text-xs text-muted-foreground mb-1.5">Uso real (7d):</p>
                      {usageList.slice(0, 3).map((m) => (
                        <div key={m.model} className="flex items-center justify-between text-xs">
                          <span className="font-mono truncate">{m.model.split("/").pop()}</span>
                          <Badge variant={m.successRate >= 95 ? "default" : m.successRate >= 80 ? "secondary" : "destructive"} className="text-[10px]">
                            {m.successRate}% · {m.avgMs}ms
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <EditPolicyDialog
        policy={editing}
        onClose={() => setEditing(null)}
        onSave={(changes) => editing && editMut.mutate({ id: editing.id, changes })}
        isSaving={editMut.isPending}
      />
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <Card>
      <CardContent className="py-12 text-center text-muted-foreground text-sm">{message}</CardContent>
    </Card>
  );
}

function EditPolicyDialog({
  policy, onClose, onSave, isSaving,
}: {
  policy: PolicyRow | null;
  onClose: () => void;
  onSave: (changes: Record<string, unknown>) => void;
  isSaving: boolean;
}) {
  const [form, setForm] = useState<Record<string, string>>({});

  // Reset form when policy changes
  useMemo(() => {
    if (policy) {
      setForm({
        preferred_provider: policy.preferred_provider,
        preferred_model: policy.preferred_model,
        fallback_provider: policy.fallback_provider ?? "",
        fallback_model: policy.fallback_model ?? "",
        temperature_default: policy.temperature_default?.toString() ?? "0.7",
        max_tokens: policy.max_tokens?.toString() ?? "4096",
        sla_max_latency_ms: policy.sla_max_latency_ms?.toString() ?? "",
      });
    }
  }, [policy?.id]);

  if (!policy) return null;

  const handleSave = () => {
    const changes: Record<string, unknown> = {
      preferred_provider: form.preferred_provider,
      preferred_model: form.preferred_model,
      fallback_provider: form.fallback_provider || null,
      fallback_model: form.fallback_model || null,
      temperature_default: parseFloat(form.temperature_default || "0.7"),
      max_tokens: parseInt(form.max_tokens || "4096", 10),
    };
    if (form.sla_max_latency_ms) changes.sla_max_latency_ms = parseInt(form.sla_max_latency_ms, 10);
    onSave(changes);
  };

  return (
    <Dialog open={!!policy} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Editar política — <Badge variant="secondary">{policy.task_class}</Badge></DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Provedor preferido</Label>
              <Input value={form.preferred_provider ?? ""} onChange={(e) => setForm({ ...form, preferred_provider: e.target.value })} />
            </div>
            <div>
              <Label>Modelo preferido</Label>
              <Input value={form.preferred_model ?? ""} onChange={(e) => setForm({ ...form, preferred_model: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Provedor fallback</Label>
              <Input value={form.fallback_provider ?? ""} onChange={(e) => setForm({ ...form, fallback_provider: e.target.value })} />
            </div>
            <div>
              <Label>Modelo fallback</Label>
              <Input value={form.fallback_model ?? ""} onChange={(e) => setForm({ ...form, fallback_model: e.target.value })} />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Temperatura</Label>
              <Input type="number" step="0.1" min="0" max="2" value={form.temperature_default ?? ""} onChange={(e) => setForm({ ...form, temperature_default: e.target.value })} />
            </div>
            <div>
              <Label>Max tokens</Label>
              <Input type="number" value={form.max_tokens ?? ""} onChange={(e) => setForm({ ...form, max_tokens: e.target.value })} />
            </div>
            <div>
              <Label>SLA (ms)</Label>
              <Input type="number" placeholder="opcional" value={form.sla_max_latency_ms ?? ""} onChange={(e) => setForm({ ...form, sla_max_latency_ms: e.target.value })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={isSaving}>{isSaving ? "Salvando..." : "Salvar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
